<template>
  <div class="content">
    <base-header class="pb-6">
      <div class="row align-items-center py-4">
        <div class="col-lg-6 col-7">
          <h6 class="h2 text-white d-inline-block mb-0">
            {{ $t("modern_trade.sale_order.title") }}
          </h6>
          <nav aria-label="breadcrumb" class="d-none d-md-inline-block ml-md-4">
            <route-bread-crumb></route-bread-crumb>
          </nav>
        </div>
        <div class="col-lg-6 col-5 text-right" v-if="isEditModernTrade">
          <base-button size="md" type="neutral" @click="goCreate">
            <i class="fa fa-plus"></i> {{ $t("modern_trade.sale_order.create") }}
          </base-button>
        </div>
      </div>
    </base-header>

    <div class="container-fluid mt--6">
      <card class="no-border-card" body-classes="px-0 pb-1" footer-classes="pb-2">
        <template slot="header">
          <div class="row">
            <div class="col-md-3">
              <base-input>
                <el-select
                  v-model="filterCompany"
                  :placeholder="$t('modern_trade.sale_order.company')"
                  clearable
                  filterable
                  @change="onFilter"
                  class="w-100"
                >
                  <el-option
                    v-for="c in companies"
                    :key="c.id"
                    :label="c.name"
                    :value="c.id"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input>
                <el-select
                  v-model="filterSaleType"
                  :placeholder="$t('modern_trade.sale_order.sale_type')"
                  clearable
                  @change="onFilter"
                  class="w-100"
                >
                  <el-option :label="$t('modern_trade.sale_order.sale_type_consignment')" :value="1"></el-option>
                  <el-option :label="$t('modern_trade.sale_order.sale_type_outright')" :value="2"></el-option>
                </el-select>
              </base-input>
            </div>
            <div class="col-md-2">
              <base-input>
                <el-select
                  v-model="filterStatus"
                  :placeholder="$t('modern_trade.sale_order.status')"
                  clearable
                  @change="onFilter"
                  class="w-100"
                >
                  <el-option
                    v-for="s in statusOptions"
                    :key="s.value"
                    :label="s.label"
                    :value="s.value"
                  ></el-option>
                </el-select>
              </base-input>
            </div>
            <!-- quick filter: drafts saved from the PO editor (spec §2) -->
            <div class="col-md-1">
              <base-button
                size="md"
                block
                class="mb-4"
                :type="filterStatus === 1 ? 'primary' : 'secondary'"
                @click="toggleDraftFilter"
              >
                {{ $t("modern_trade.sale_order.status_draft") }}
              </base-button>
            </div>
            <div class="col-md-4">
              <base-input>
                <el-date-picker
                  v-model="dateRange"
                  type="daterange"
                  value-format="yyyy-MM-dd"
                  range-separator="-"
                  :start-placeholder="$t('modern_trade.sale_order.created_from')"
                  :end-placeholder="$t('modern_trade.sale_order.created_to')"
                  class="w-100"
                  @change="onFilter"
                ></el-date-picker>
              </base-input>
            </div>
          </div>
          <div class="row">
            <div class="col-md-6">
              <el-input
                v-model="search"
                :placeholder="$t('search')"
                clearable
                @keyup.enter.native="onFilter"
              >
                <el-button slot="append" type="primary" @click="onFilter">
                  <i class="fa fa-search"></i> {{ $t("search") }}
                </el-button>
              </el-input>
            </div>
            <!-- round-19 Dev-C: global document-number search -->
            <div class="col-md-6">
              <mt-doc-search-box></mt-doc-search-box>
            </div>
          </div>
        </template>

        <div v-loading="loading">
          <el-table :data="rows" row-key="id" header-row-class-name="thead-light">
            <el-table-column :label="$t('modern_trade.sale_order.so_no')" min-width="150">
              <template slot-scope="{ row }">
                <a href="javascript:void(0)" @click="goDetail(row.id)"><strong>{{ row.so_no }}</strong></a>
                <i
                  class="fa fa-file-pdf text-danger ml-2"
                  style="cursor:pointer"
                  :title="$t('modern_trade.sale_order.view_pdf')"
                  @click="openPoPdf(row)"
                ></i>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.customer')" min-width="240">
              <template slot-scope="{ row }">
                <div class="d-flex align-items-center justify-content-between">
                  <div>
                    <div><strong>{{ row.company_name || row.company_id }}</strong></div>
                    <small class="text-muted">
                      {{ row.tax_id || "-" }}<span v-if="row.branch_code"> · {{ row.branch_code }}</span>
                    </small>
                  </div>
                  <base-button
                    size="sm"
                    type="secondary"
                    class="py-1 px-2 ml-2"
                    :title="$t('modern_trade.sale_order.view_customer')"
                    @click.native="openCustomer(row)"
                  >
                    <i class="fa fa-info-circle"></i>
                  </base-button>
                </div>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.po_number')" min-width="120">
              <template slot-scope="{ row }">{{ row.po_number }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.net_amount')" min-width="120" align="right">
              <template slot-scope="{ row }">{{ money(row.net_amount) }}</template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.status')" min-width="120" align="center">
              <template slot-scope="{ row }">
                <span class="mt-pill" :style="mtStatusStyle(row.status)">{{ mtStatusLabel(row.status) }}</span>
              </template>
            </el-table-column>
            <el-table-column :label="$t('modern_trade.sale_order.created_at') || 'Created'" min-width="120">
              <template slot-scope="{ row }">{{ formatDate(row.created_at) }}</template>
            </el-table-column>
            <el-table-column :label="$t('actions')" min-width="90" align="center">
              <template slot-scope="{ row }">
                <base-button size="sm" type="info" @click.native="goDetail(row.id)">
                  <i class="fa fa-eye"></i>
                </base-button>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <div slot="footer" class="d-flex align-items-center justify-content-between">
          <p class="text-muted mb-0">{{ $t("total") }}: {{ total }}</p>
          <base-pagination
            v-model="page"
            :per-page="perPage"
            :total="total"
            @input="loadData"
          ></base-pagination>
        </div>
      </card>
    </div>

    <!-- Shared customer detail (#164) -->
    <customer-detail-modal
      :visible.sync="showCustomer"
      :company-id="customerId"
    ></customer-detail-modal>
  </div>
</template>

<script>
import { Table, TableColumn, Select, Option, Button, Input, DatePicker } from "element-ui";
import RouteBreadCrumb from "@/components/Breadcrumb/RouteBreadcrumb";
import { BasePagination } from "@/components";
import CustomerDetailModal from "@/components/Modern/CustomerDetailModal";
import MtDocSearchBox from "@/views/ModernTrade/MtDocSearchBox";
import mtStatusColor from "@/mixins/mtStatusColor";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { ModernTradeService } from "@/resource";

export default {
  name: "MtSaleOrders",
  mixins: [mtStatusColor],
  components: {
    RouteBreadCrumb,
    BasePagination,
    CustomerDetailModal,
    MtDocSearchBox,
    [Table.name]: Table,
    [TableColumn.name]: TableColumn,
    [Select.name]: Select,
    [Option.name]: Option,
    [Button.name]: Button,
    [Input.name]: Input,
    [DatePicker.name]: DatePicker,
  },
  computed: {
    ...mapGetters({
      isAccessModernTrade: "Auth/isAccessModernTrade",
      isEditModernTrade: "Auth/isEditModernTrade",
    }),
  },
  data() {
    return {
      loading: false,
      rows: [],
      total: 0,
      page: 1,
      perPage: 20,
      search: "",
      filterCompany: null,
      filterSaleType: null,
      filterStatus: null,
      dateRange: null,
      companies: [],
      // shared customer modal
      showCustomer: false,
      customerId: null,
      statusOptions: [],
    };
  },
  mounted() {
    this.buildStatusOptions();
    this.restoreFromQuery();
    this.loadCompanies();
    this.loadData();
  },
  methods: {
    // Restore filter + page from the URL query so returning from the detail
    // page (via $router.back) lands on the exact same search state (#164).
    restoreFromQuery() {
      const q = this.$route.query || {};
      if (q.search) this.search = q.search;
      if (q.company_id) this.filterCompany = Number(q.company_id) || q.company_id;
      if (q.sale_type) this.filterSaleType = Number(q.sale_type);
      if (q.status) this.filterStatus = Number(q.status);
      if (q.created_from && q.created_to) this.dateRange = [q.created_from, q.created_to];
      if (q.page) this.page = Number(q.page) || 1;
    },
    syncQuery() {
      const query = {};
      if (this.search) query.search = this.search;
      if (this.filterCompany) query.company_id = this.filterCompany;
      if (this.filterSaleType) query.sale_type = this.filterSaleType;
      if (this.filterStatus) query.status = this.filterStatus;
      if (this.dateRange && this.dateRange.length === 2) {
        query.created_from = this.dateRange[0];
        query.created_to = this.dateRange[1];
      }
      if (this.page > 1) query.page = this.page;
      this.$router.replace({ query }).catch(() => {});
    },
    // #183: open the full-screen print-set preview page instead of downloading.
    // ไอคอน PDF ตัวนี้อยู่ข้างเลขที่ใบสั่งขายในตาราง ⇒ ระบุ doc=so ให้ชัดเจน ไม่พึ่ง default
    // ของหน้า preview (ถ้าวันหลัง default เปลี่ยน ปุ่มนี้ต้องยังเปิดใบสั่งขายเหมือนเดิม)
    openPoPdf(row) {
      this.$router.push({
        name: "MtPrintPreview",
        params: { soId: String(row.id) },
        query: { so_no: row.so_no, doc: "so" },
      });
    },
    openCustomer(row) {
      this.customerId = row.company_id;
      this.showCustomer = true;
    },
    buildStatusOptions() {
      this.statusOptions = [
        { value: 1, label: this.$t("modern_trade.sale_order.status_draft") },
        { value: 2, label: this.$t("modern_trade.sale_order.status_confirmed") },
        { value: 3, label: this.$t("modern_trade.sale_order.status_in_production") },
        { value: 4, label: this.$t("modern_trade.sale_order.status_goods_received") },
        { value: 5, label: this.$t("modern_trade.sale_order.status_delivered") },
        { value: 6, label: this.$t("modern_trade.sale_order.status_closed") },
        { value: 9, label: this.$t("modern_trade.sale_order.status_cancelled") },
      ];
    },
    async loadCompanies() {
      try {
        const res = await ModernTradeService.companiesList({ page: 1, per_page: 500, status: 1 });
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.companies = d.items || [];
      } catch (err) {
        // Non-fatal — filter/select simply stay empty.
      }
    },
    async loadData() {
      this.loading = true;
      try {
        const params = { page: this.page, per_page: this.perPage };
        if (this.filterCompany) params.company_id = this.filterCompany;
        if (this.filterSaleType) params.sale_type = this.filterSaleType;
        if (this.filterStatus) params.status = this.filterStatus;
        if (this.search) params.search = this.search;
        if (this.dateRange && this.dateRange.length === 2) {
          params.created_from = this.dateRange[0];
          params.created_to = this.dateRange[1];
        }
        this.syncQuery();
        const res = await ModernTradeService.saleOrdersList(params);
        const d = (res.data && res.data.Data && res.data.Data[0]) || {};
        this.rows = d.items || [];
        this.total = d.total || 0;
      } catch (err) {
        swal.fire({ title: (err.response && err.response.data && err.response.data.Cause) || err.message, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    onFilter() {
      this.page = 1;
      this.loadData();
    },
    goDetail(id) {
      this.$router.push({ name: "MtSaleOrderDetail", params: { id } });
    },
    goCreate() {
      this.$router.push({ name: "MtSaleOrderNew" });
    },
    toggleDraftFilter() {
      this.filterStatus = this.filterStatus === 1 ? null : 1;
      this.onFilter();
    },
    saleTypeLabel(t) {
      if (t === 1) return this.$t("modern_trade.sale_order.sale_type_consignment");
      if (t === 2) return this.$t("modern_trade.sale_order.sale_type_outright");
      return "-";
    },
    shippingTypeLabel(t) {
      switch (t) {
        case 1: return this.$t("modern_trade.sale_order.shipping_type_pack");
        case 2: return this.$t("modern_trade.sale_order.shipping_type_factory");
        case 3: return this.$t("modern_trade.sale_order.shipping_type_express");
        default: return "-";
      }
    },
    statusLabel(s) {
      switch (s) {
        case 1: return this.$t("modern_trade.sale_order.status_draft");
        case 2: return this.$t("modern_trade.sale_order.status_confirmed");
        case 3: return this.$t("modern_trade.sale_order.status_in_production");
        case 4: return this.$t("modern_trade.sale_order.status_goods_received");
        case 5: return this.$t("modern_trade.sale_order.status_delivered");
        case 6: return this.$t("modern_trade.sale_order.status_closed");
        case 9: return this.$t("modern_trade.sale_order.status_cancelled");
        default: return "-";
      }
    },
    statusClass(s) {
      switch (s) {
        case 1: return "badge-secondary";
        case 2: return "badge-info";
        case 3: return "badge-warning";
        case 4: return "badge-warning";
        case 5: return "badge-primary";
        case 6: return "badge-success";
        case 9: return "badge-danger";
        default: return "badge-secondary";
      }
    },
    round2(v) {
      return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
    },
    money(v) {
      const n = Number(v) || 0;
      return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    formatDate(v) {
      if (!v) return "-";
      const d = new Date(v);
      if (isNaN(d.getTime())) return v;
      const pad = n => String(n).padStart(2, "0");
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    },
  },
};
</script>

<style scoped>
.mt-pill {
  display: inline-block;
  padding: 2px 10px;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  line-height: 1.4;
  white-space: nowrap;
}
</style>
